import { useEffect, useRef, useState, type JSX } from 'react';
import { Copy, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useRevealAccountPassword } from '@/hooks/useOutreachAccounts';
import { REVEAL_SECONDS } from '@/lib/outreachAccounts';
import type { OutreachAccount } from '@/api/types';

const MASK = '••••••••';

/**
 * Clear the clipboard after a copy, unless the admin has copied something
 * else since. If the browser won't let us read it back, clear it anyway: a
 * password left on the clipboard is the bigger risk. Not tied to the
 * component, so leaving the page doesn't cancel it.
 */
function scheduleClipboardClear(value: string): void {
  setTimeout(() => {
    void (async () => {
      try {
        const current = await navigator.clipboard.readText();
        if (current !== value) return;
      } catch {
        // Can't read the clipboard: fall through and clear.
      }
      try {
        await navigator.clipboard.writeText('');
      } catch {
        // Nothing more we can do.
      }
    })();
  }, REVEAL_SECONDS * 1000);
}

/**
 * The password column: masked, with Reveal (shows it for REVEAL_SECONDS,
 * then re-masks) and Copy (fetches it straight to the clipboard without
 * showing it, then clears the clipboard). Each one is an audited call.
 */
export function PasswordCell({ account }: { account: OutreachAccount }): JSX.Element {
  const reveal = useRevealAccountPassword();
  const [shown, setShown] = useState<string | null>(null);
  const [busy, setBusy] = useState<'REVEAL' | 'COPY' | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  if (!account.hasPassword) {
    return <span className="text-xs text-muted-foreground">No password</span>;
  }

  const handle = `@${account.username}`;

  function hide(): void {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setShown(null);
  }

  async function onReveal(): Promise<void> {
    if (shown !== null) {
      hide();
      return;
    }
    setBusy('REVEAL');
    try {
      const password = await reveal(account.publicId, 'REVEAL');
      setShown(password);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        setShown(null);
      }, REVEAL_SECONDS * 1000);
    } catch (err) {
      toast.error(`Couldn't reveal the password for ${handle}: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  async function onCopy(): Promise<void> {
    setBusy('COPY');
    try {
      const password = await reveal(account.publicId, 'COPY');
      await navigator.clipboard.writeText(password);
      scheduleClipboardClear(password);
      toast.success(`Copied the password for ${handle}. It clears from the clipboard in ${REVEAL_SECONDS} seconds.`);
    } catch (err) {
      toast.error(`Couldn't copy the password for ${handle}: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex items-center gap-1">
      {shown !== null ? (
        <code
          className="max-w-[16rem] select-all truncate rounded bg-muted px-1.5 py-0.5 font-mono text-xs"
          data-testid="revealed-password"
          title={`Hides again in ${REVEAL_SECONDS} seconds`}
        >
          {shown}
        </code>
      ) : (
        <span aria-label="Password hidden" className="font-mono text-xs tracking-wider text-muted-foreground">
          {MASK}
        </span>
      )}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        onClick={() => void onReveal()}
        disabled={busy !== null}
        aria-label={shown !== null ? `Hide password for ${handle}` : `Reveal password for ${handle}`}
        title={shown !== null ? 'Hide' : `Reveal for ${REVEAL_SECONDS} seconds`}
      >
        {busy === 'REVEAL' ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : shown !== null ? (
          <EyeOff className="h-3.5 w-3.5" />
        ) : (
          <Eye className="h-3.5 w-3.5" />
        )}
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        onClick={() => void onCopy()}
        disabled={busy !== null}
        aria-label={`Copy password for ${handle}`}
        title={`Copy (clears from the clipboard after ${REVEAL_SECONDS} seconds)`}
      >
        {busy === 'COPY' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}
